import { useEffect, useRef, useState } from "react";
import { useLocalParticipant } from "@livekit/components-react";
import { Mic } from "lucide-react";

const STORAGE_KEY = "ctoa_push_to_talk_enabled";
const PTT_KEY = "Space";

function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
}

// Optional push-to-talk for quieter tables — off by default (mic stays always-on, LiveKit's
// normal behavior). When on, the mic is muted until the Knight holds SPACE, so nothing gets
// picked up between turns. Purely a client-side preference (localStorage), no backend needed.
export default function PushToTalkControl() {
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const [enabled, setEnabled] = useState(() => localStorage.getItem(STORAGE_KEY) === "true");
  const heldRef = useRef(false);
  const prevEnabledRef = useRef(enabled);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, String(enabled)); }, [enabled]);

  // Real ON/OFF transitions snap to the right resting state immediately.
  useEffect(() => {
    if (!localParticipant || prevEnabledRef.current === enabled) return;
    prevEnabledRef.current = enabled;
    localParticipant.setMicrophoneEnabled(!enabled);
    heldRef.current = false;
  }, [enabled, localParticipant]);

  // Continuous guard: LiveKitRoom publishes its own initial mic track asynchronously, which can
  // land AFTER the transition above and leave a "hot mic" while PTT is enabled and not held —
  // this re-asserts muted every time LiveKit's own state drifts back to enabled while resting.
  useEffect(() => {
    if (!localParticipant || !enabled || heldRef.current) return;
    if (isMicrophoneEnabled) localParticipant.setMicrophoneEnabled(false);
  }, [enabled, isMicrophoneEnabled, localParticipant]);

  useEffect(() => {
    if (!enabled || !localParticipant) return;

    const onKeyDown = (e) => {
      if (e.code !== PTT_KEY || e.repeat || isTypingTarget(document.activeElement)) return;
      e.preventDefault();
      if (heldRef.current) return;
      heldRef.current = true;
      localParticipant.setMicrophoneEnabled(true);
    };
    const onKeyUp = (e) => {
      if (e.code !== PTT_KEY || !heldRef.current) return;
      heldRef.current = false;
      localParticipant.setMicrophoneEnabled(false);
    };
    // Releasing the key outside the window (alt-tab, etc.) shouldn't leave the mic stuck open.
    const onBlur = () => {
      if (!heldRef.current) return;
      heldRef.current = false;
      localParticipant.setMicrophoneEnabled(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [enabled, localParticipant]);

  if (!localParticipant) return null;

  return (
    <div data-testid="push-to-talk-control" className="flex items-center gap-2.5 px-3 py-2 border-b border-white/10 shrink-0">
      <button
        data-testid="push-to-talk-toggle"
        onClick={() => setEnabled((v) => !v)}
        role="switch"
        aria-checked={enabled}
        className={`relative w-9 h-5 rounded-full shrink-0 transition-colors duration-200 ${enabled ? "bg-[#D4AF37]" : "bg-white/15"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200 ${enabled ? "translate-x-4" : ""}`} />
      </button>
      <span className="text-xs text-[#f1e0c5]/70 flex-1">
        Push-to-Talk {enabled && <span className="text-[#f1e0c5]/40">— hold SPACE</span>}
      </span>
      {enabled && (
        <span
          data-testid="push-to-talk-active-indicator"
          className={`flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full shrink-0 transition-colors duration-150 ${
            isMicrophoneEnabled ? "bg-[#D90429] text-white" : "bg-white/5 text-[#f1e0c5]/30"
          }`}
        >
          <Mic className="w-2.5 h-2.5" /> {isMicrophoneEnabled ? "Talking" : "Muted"}
        </span>
      )}
      {!enabled && isMicrophoneEnabled === false && (
        <span className="text-[10px] uppercase tracking-wider text-[#f1e0c5]/30">Mic muted</span>
      )}
    </div>
  );
}
